import { type FC, useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { Modal } from '@components/modal';
import { Button } from 'layout/Button';
import {
  useCreateAssetNote,
  useDeleteAssetNote,
  useListAssetNotes,
  useUpdateAssetNote,
} from 'api/assets/assetsServices';
import { NoteItem } from './NoteItem';
import { NoteForm } from './NoteForm';

export interface Note {
  id: string;
  date: string;
  description: string;
  letter_index?: string;
  created_by_user: {
    given_name: string;
    family_name: string;
  };
  created_at: string;
  updated_at: string;
}

interface NotesModalProps {
  isOpen: boolean;
  handleClose: () => void;
  id?: string;
}

const NotesModal: FC<NotesModalProps> = ({
  isOpen,
  handleClose,
  id: assetId,
}) => {
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | number | undefined>(
    undefined
  );

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setEditingNote(null);
      setIsCreating(false);
      setDeleteNoteId(undefined);
    }
  }, [isOpen]);

  // Fetch notes
  const {
    data: notesData,
    isLoading,
    isError,
    isFetching,
  } = useListAssetNotes(assetId);
  console.log('Notes data modal:', notesData);
  const notes = notesData?.notes || [];

  // Mutations
  const createNoteMutation = useCreateAssetNote(assetId);
  const updateNoteMutation = useUpdateAssetNote(assetId, editingNote?.id);
  const deleteNoteMutation = useDeleteAssetNote(assetId, deleteNoteId);

  const handleSaveNote = (
    noteData: Omit<Note, 'id' | 'created_by_user' | 'created_at' | 'updated_at'>
  ): void => {
    if (!assetId) return;
    const payload = {
      description: noteData.description,
      date: noteData.date,
    };
    if (editingNote) {
      updateNoteMutation.mutate(payload, {
        onSuccess: () => {
          setEditingNote(null);
          setIsCreating(false);
        },
      });
    } else {
      createNoteMutation.mutate(payload, {
        onSuccess: () => {
          setIsCreating(false);
        },
      });
    }
  };

  const handleEdit = (note: Note): void => {
    setEditingNote(note);
    setIsCreating(true);
  };

  const handleDelete = (id: string): void => {
    setDeleteNoteId(id);
    deleteNoteMutation.mutate(undefined, {
      onSuccess: () => {
        setEditingNote(null);
        setIsCreating(false);
        setDeleteNoteId(undefined);
      },
      onError: () => {
        setDeleteNoteId(undefined);
      },
    });
  };

  const handleAddNew = (): void => {
    setEditingNote(null);
    setIsCreating(true);
  };

  const handleCancel = (): void => {
    setEditingNote(null);
    setIsCreating(false);
  };

  const hasNotes = notes?.length > 0;
  const isAnyMutationLoading =
    createNoteMutation.isPending ||
    updateNoteMutation.isPending ||
    deleteNoteMutation.isPending;

  const shouldShowLoadingSkeleton = isLoading || isFetching;

  const createNotesSkeleton = (): React.ReactNode => {
    return (
      <div className="flex gap-2 items-start">
        <Skeleton height={24} width={24} circle />
        <div className="p-4 border rounded-lg w-full">
          <div className="flex justify-between items-center mb-2.5">
            <Skeleton width={200} height={12} />
            <div className="flex gap-2">
              <Skeleton width={16} height={16} />
              <Skeleton width={16} height={16} />
            </div>
          </div>
          <Skeleton count={2} height={16} />
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Notes" maxWidth={600}>
      <div className="flex items-center flex-col gap-4">
        {isError && (
          <div className="text-center text-red-500">Failed to load notes.</div>
        )}
        {!isLoading && !hasNotes && !isCreating && (
          <div className="text-center text-gray-500">
            <p>No notes yet. Add one to keep track of important info.</p>
          </div>
        )}
        <div
          className={clsx(
            'flex w-full',
            hasNotes || isCreating ? 'justify-end' : 'justify-center'
          )}
        >
          <Button
            onClick={handleAddNew}
            label="New note"
            icon={<FaPlus />}
            isLoading={isAnyMutationLoading}
            disabled={isAnyMutationLoading}
          />
        </div>
        {isCreating ? (
          <NoteForm
            onSave={handleSaveNote}
            onCancel={handleCancel}
            note={editingNote}
            isLoading={isAnyMutationLoading}
          />
        ) : (
          <>
            <div className="flex flex-col gap-4 w-full">
              {shouldShowLoadingSkeleton && (
                <div className="flex flex-col gap-4 w-full">
                  {createNotesSkeleton()}
                </div>
              )}
              {hasNotes &&
                !shouldShowLoadingSkeleton &&
                notes?.map((note: Note, index: number) => (
                  <NoteItem
                    key={note.id}
                    note={note}
                    noteIndex={index}
                    onEdit={handleEdit}
                    onDelete={() => {
                      handleDelete(note.id);
                    }}
                    isDeleting={
                      deleteNoteId === note.id && deleteNoteMutation.isPending
                    }
                  />
                ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export { NotesModal };
export default NotesModal;
